import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { IJwtIdTokenClaims, IUser } from '@cod-monorepo/interfaces';
import { Store } from '@ngxs/store';
import { SetUser } from '../../user-management/state/user-management.action';
import { UserManagementService } from '../../user-management/services/user-management.service';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Roles } from '@cod-monorepo/interfaces';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    public isAuthenticated$: Observable<boolean>;
    private _authUser: BehaviorSubject<IJwtIdTokenClaims | undefined> = new BehaviorSubject<
        IJwtIdTokenClaims | undefined
    >(undefined);
    public authUser$: Observable<IJwtIdTokenClaims | undefined> = this._authUser.pipe(filter(user => Boolean(user)));

    constructor(
        private securityService: OidcSecurityService,
        private store: Store,
        private userService: UserManagementService,
        private router: Router,
        private httpClient: HttpClient
    ) {
        this.isAuthenticated$ = this.securityService.checkAuth().pipe(
            filter(auth => !!auth?.userData),
            tap(() => this.fetchUser()),
            map(auth => auth.isAuthenticated)
        );
    }

    get authUser() {
        return this._authUser.getValue();
    }

    get accessToken$() {
        return this.securityService.getAccessToken();
    }

    public login(): void {
        const url = window.location.pathname + window.location.search;
        window.localStorage.setItem('redirectUrl', url);
        this.securityService.authorize();
    }

    public logout(): void {
        this.securityService
            .isAuthenticated()
            .pipe(
                filter(isAuthenticated => isAuthenticated),
                switchMap(() => this.securityService.logoffAndRevokeTokens()),
                catchError(err => of(console.error(err)))
            )
            .subscribe();
    }

    private fetchUser(): void {
        this.userService
            .getMe()
            .pipe(
                tap(user => {
                    this.dispatchUser(user);
                    if (user?.roles?.includes(Roles.ADMIN)) {
                        this.httpClient
                            .post(`${environment.apiUrl}/me/swagger-session`, undefined, { withCredentials: true })
                            .subscribe();
                    }
                }),
                catchError(err => {
                    if (err?.status === 404) {
                        this.router.navigate(['/auth/unauthorized']);
                    }

                    return err;
                })
            )
            .subscribe();
    }

    private dispatchUser(user: IUser): void {
        this.store.dispatch(
            new SetUser({
                user
            })
        );
    }
}
